import {
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  TextField
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { makeToasterErrorHandler } from "../../common/http/makeToasterErrorHandler";
import { SubscriptionConfigInterface } from "../../common/interfaces";
import { useAuth } from "../../hooks/useAuth";
import { usePageToast } from "../../hooks/usePageToast";
import api from "../../services/api";
import {
  TypeCompanySubscriptionType,
  TypeCompanySubscriptionTypeLabel
} from "../enum/typeCompanySubscriptionTypeEnum";
import {
  TypeSubscriptionStatus,
  TypeSubscriptionStatusLabel
} from "../enum/typeSubscriptionStatusEnum";
import PageToaster from "../toaster/PageToaster";

/** Carência mínima entre a criação da assinatura e o primeiro vencimento. */
const FIRST_DUE_DATE_MIN_DAYS_AHEAD = 7;

/**
 * Espelha CustomDate.getFirstAmazonDueDate do back-end: se o dia escolhido já
 * passou, é hoje, ou está a até 7 dias de distância, o primeiro lançamento vai
 * para o mês seguinte. Domingo vence na segunda-feira seguinte.
 *
 * Este espelho é só informativo — quem decide é o back-end. Se a regra mudar,
 * mude nos dois lados.
 */
const previewFirstDueDate = (
  dueDay: number,
  reference: Date = new Date()
): Date | null => {
  if (!Number.isInteger(dueDay) || dueDay < 1 || dueDay > 20) return null;

  const today = new Date(
    reference.getFullYear(),
    reference.getMonth(),
    reference.getDate()
  );

  const buildDueDate = (monthOffset: number) => {
    const lastDayOfMonth = new Date(
      today.getFullYear(),
      today.getMonth() + monthOffset + 1,
      0
    ).getDate();

    const target = new Date(
      today.getFullYear(),
      today.getMonth() + monthOffset,
      Math.min(dueDay, lastDayOfMonth)
    );

    // Domingo (0) vence na segunda-feira seguinte.
    if (target.getDay() === 0) target.setDate(target.getDate() + 1);

    return target;
  };

  const daysAhead = (target: Date) =>
    Math.round((target.getTime() - today.getTime()) / 86400000);

  let monthOffset = 0;
  let target = buildDueDate(monthOffset);

  while (
    daysAhead(target) <= FIRST_DUE_DATE_MIN_DAYS_AHEAD &&
    monthOffset < 3
  ) {
    monthOffset += 1;
    target = buildDueDate(monthOffset);
  }

  return target;
};

interface SubscriptionCreateModalProps {
  open: boolean;
  onClose: () => void;
  onSubscriptionSaved: () => void;
  subscription?: SubscriptionConfigInterface | null;
}

const SubscriptionCreateModal: React.FC<SubscriptionCreateModalProps> = ({
  open,
  onClose,
  onSubscriptionSaved,
  subscription
}) => {
  const { isAdmin } = useAuth();
  // const [companyId, setCompanyId] = useState<number>(0);
  const [companySubscriptionType, setCompanySubscriptionType] =
    useState<TypeCompanySubscriptionType>(TypeCompanySubscriptionType.NONE);
  const [statusCompany, setStatusCompany] = useState<TypeSubscriptionStatus>(
    TypeSubscriptionStatus.PENDING
  );
  const [amount, setAmount] = useState<number>(0);
  const [dueDay, setDueDay] = useState<number>(1);

  const [updateMode, setUpdateMode] = useState(false);

  const containerId = "toaster-subscription-create-modal";
  const { autoSuccess, autoError, autoInfo, autoWarn } = usePageToast(
    containerId,
    true
  );

  const [companies, setCompanies] = useState<any[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<any>(null); // Inicialize como null se 'THUNNAR' não for um valor padrão desejado sempre
  const [loadingCompanies, setLoadingCompanies] = useState<boolean>(false);

  const handleHttpError = makeToasterErrorHandler(autoError);

  const firstDueDatePreview = useMemo(
    () => previewFirstDueDate(dueDay),
    [dueDay]
  );

  useEffect(() => {
    const url = isAdmin ? "/company/admin" : "/company";

    setLoadingCompanies(true);
    api
      .get(url)
      .then((r) => setCompanies(r.data.items || r.data))
      .catch(console.error)
      .finally(() => setLoadingCompanies(false));
  }, [isAdmin]);

  const clear = () => {
    setUpdateMode(false);
    // setCompanyId(0);
    setSelectedCompany(null);
    setCompanySubscriptionType(TypeCompanySubscriptionType.NONE);
    setStatusCompany(TypeSubscriptionStatus.PENDING);
    setAmount(0);
    setDueDay(1);
  };

  useEffect(() => {
    setTimeout(() => {
      if (subscription) {
        setUpdateMode(true);
        // setCompanyId(subscription.company_id);

        setSelectedCompany(
          companies.find((c) => c.company_id === subscription.company_id) ||
            null
        );
        setCompanySubscriptionType(
          subscription.company_subscription_type ??
            TypeCompanySubscriptionType.NONE
        );
        setStatusCompany(subscription.status ?? TypeSubscriptionStatus.PENDING);
        setAmount(Number(subscription.amount) || 0);
        setDueDay(subscription.due_day ?? 1);
      } else {
        clear();
      }
    }, 500);
  }, [subscription]);

  const handleClose = () => {
    clear();
    onClose();
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        company_id: selectedCompany?.company_id,
        company_subscription_type: companySubscriptionType,
        status_company: statusCompany,
        amount,
        due_day: dueDay
      };

      if (updateMode && subscription?.config_id) {
        await api.put(
          `/subscription/config/${subscription.config_id}`,
          payload
        );
        autoSuccess("Assinatura atualizada com sucesso!");
      } else {
        await api.post("/subscription/config", payload);
        autoSuccess("Assinatura criada com sucesso!");
      }

      onSubscriptionSaved();
      clear();
      onClose();
    } catch (error: any) {
      handleHttpError(error);
    }
  };

  return (
    <>
      <PageToaster containerId={containerId} />
      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            border: "1px solid var(--border-strong)",
            p: 3,
            borderRadius: 2,
            boxShadow: 3,
            width: 400,
            maxHeight: "90vh",
            overflowY: "auto"
          }}
        >
          <h2>{updateMode ? "Editar Assinatura" : "Criar Assinatura"}</h2>

          {/* <TextField
            fullWidth
            label="ID da Empresa"
            type="number"
            value={companyId}
            onChange={(e) => setCompanyId(Number(e.target.value))}
            margin="normal"
            required
          /> */}

          <Autocomplete
            sx={{ width: 400 }}
            options={companies}
            getOptionLabel={(opt) => opt.company_name || ""}
            value={selectedCompany}
            isOptionEqualToValue={(option, value) =>
              option?.company_id === value?.company_id
            }
            onChange={(_, v) => setSelectedCompany(v)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Empresa"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingCompanies && (
                        <CircularProgress color="inherit" size={20} />
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
              />
            )}
          />

          <FormControl fullWidth margin="normal">
            <InputLabel>Tipo de Assinatura</InputLabel>
            <Select
              value={companySubscriptionType}
              label="Tipo de Assinatura"
              onChange={(e) =>
                setCompanySubscriptionType(
                  e.target.value as TypeCompanySubscriptionType
                )
              }
            >
              {(
                Object.values(
                  TypeCompanySubscriptionType
                ) as TypeCompanySubscriptionType[]
              )
                .filter((t) => t !== TypeCompanySubscriptionType.NONE)
                .map((t) => (
                  <MenuItem key={t} value={t}>
                    {TypeCompanySubscriptionTypeLabel[t]}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          {false && (
            <FormControl fullWidth margin="normal">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusCompany}
                label="Status"
                onChange={(e) =>
                  setStatusCompany(e.target.value as TypeSubscriptionStatus)
                }
              >
                {(
                  Object.values(
                    TypeSubscriptionStatus
                  ) as TypeSubscriptionStatus[]
                ).map((s) => (
                  <MenuItem key={s} value={s}>
                    {TypeSubscriptionStatusLabel[s]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <TextField
            fullWidth
            label="Valor (R$)"
            type="number"
            value={amount}
            onChange={(e) => setAmount(Number(e.target.value))}
            margin="normal"
            required
          />

          <TextField
            fullWidth
            label="Dia de Vencimento"
            type="number"
            inputProps={{ min: 1, max: 20 }}
            value={dueDay}
            onChange={(e) => setDueDay(Number(e.target.value))}
            margin="normal"
            required
          />

          <Alert severity="info" sx={{ mt: 1 }}>
            <AlertTitle>Como o primeiro vencimento é calculado</AlertTitle>
            Se o dia escolhido <strong>já passou</strong>, é{" "}
            <strong>hoje</strong> ou está a{" "}
            <strong>até {FIRST_DUE_DATE_MIN_DAYS_AHEAD} dias</strong>, o
            primeiro lançamento é gerado para o{" "}
            <strong>mês seguinte</strong>. Ele só fica no mês atual quando
            faltarem{" "}
            <strong>mais de {FIRST_DUE_DATE_MIN_DAYS_AHEAD} dias</strong>.
            Vencimento em domingo é movido para a segunda-feira seguinte.
            <Box sx={{ mt: 1, fontSize: 13 }}>
              A regra vale apenas para o <strong>primeiro</strong> lançamento;
              os seguintes seguem o ciclo mensal normal.
            </Box>
            {!updateMode && firstDueDatePreview && (
              <Box sx={{ mt: 1 }}>
                Primeiro vencimento previsto:{" "}
                <strong>
                  {firstDueDatePreview.toLocaleDateString("pt-BR")}
                </strong>
              </Box>
            )}
          </Alert>

          <Box sx={{ mt: 3, textAlign: "right" }}>
            <Button onClick={handleClose} sx={{ mr: 1 }}>
              Cancelar
            </Button>
            <Button variant="contained" onClick={handleSubmit}>
              {updateMode ? "Atualizar" : "Criar"}
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default SubscriptionCreateModal;
